import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { switchMap, catchError, finalize, map } from 'rxjs/operators';

import { AdminWallet } from '../interfaces/admin-wallet.interface';
import { CreateAdminWalletDto } from '../interfaces/create-admin-wallet.dto';
import { UpdateAdminWalletDto } from '../interfaces/update-admin-wallet.dto';
import { QueryAdminWalletDto } from '../interfaces/query-admin-wallet.dto';
import { CognitoService } from 'src/app/auth/services/cognito.service';

@Injectable({
  providedIn: 'root',
})
export class AdminWalletsService {
  private apiUrl = `${environment.apiNest}wallets-control`;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  // Nuevo método que detecta si el endpoint es público
  private async getHeadersIfNeeded(endpointUrl: string): Promise<HttpHeaders> {
    const isPublic = endpointUrl.includes('/public/');
    if (isPublic) return new HttpHeaders();

    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    let customError: any = {
      statusCode: error.status,
      message: 'Ha ocurrido un error en el servicio',
      ...error.error,
    };

    if (error.error instanceof ErrorEvent) {
      customError.message = `Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 0:
          customError.message = `No se pudo conectar al servidor. Verifique la URL o CORS.`;
          break;
        case 401:
          customError.message =
            'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          customError.message = 'No tiene permisos para realizar esta acción.';
          break;
        case 400:
          customError.message = 'Solicitud inválida.';
          break;
        case 404:
          customError.message =
            'El servicio no está disponible en este momento.';
          break;
        case 500:
          customError.message =
            'Error interno del servidor. Por favor, intente más tarde.';
          break;
        default:
          if (error.error?.message) {
            customError.message = error.error.message;
          } else if (typeof error.error === 'string') {
            customError.message = error.error;
          } else {
            customError.message = `Error del servidor: ${error.status}, ${error.message}`;
          }
      }
    }
    return throwError(() => customError);
  }

  create(dto: CreateAdminWalletDto): Observable<AdminWallet> {
    this.loadingSubject.next(true);
    const url = this.apiUrl;
    return from(this.getHeadersIfNeeded(url)).pipe(
      switchMap((headers) =>
        this.http.post<AdminWallet>(url, dto, { headers }),
      ),
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  findAll(query?: QueryAdminWalletDto): Observable<AdminWallet[]> {
    this.loadingSubject.next(true);
    const url = `${this.apiUrl}`;
    let params = new HttpParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }
    return from(this.getHeadersIfNeeded(url)).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers, params })),
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
      map((res) => (Array.isArray(res) ? res : res?.data || [])),
    );
  }

  findOne(id: string): Observable<AdminWallet> {
    this.loadingSubject.next(true);
    const url = `${this.apiUrl}/${id}`;
    return from(this.getHeadersIfNeeded(url)).pipe(
      switchMap((headers) => this.http.get<AdminWallet>(url, { headers })),
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  update(id: string, dto: UpdateAdminWalletDto): Observable<AdminWallet> {
    this.loadingSubject.next(true);
    const url = `${this.apiUrl}/${id}`;
    return from(this.getHeadersIfNeeded(url)).pipe(
      switchMap((headers) => this.http.put<AdminWallet>(url, dto, { headers })),
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  findAllActive(query?: QueryAdminWalletDto): Observable<AdminWallet[]> {
    this.loadingSubject.next(true);
    const url = `${this.apiUrl}/public/active`;
    let params = new HttpParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }
    return from(this.getHeadersIfNeeded(url)).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers, params })),
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
      map((res) => (Array.isArray(res) ? res : res?.data || [])),
    );
  }

  findOneActive(id: string): Observable<AdminWallet> {
    this.loadingSubject.next(true);
    const url = `${this.apiUrl}/public/${id}`;
    return from(this.getHeadersIfNeeded(url)).pipe(
      switchMap((headers) => this.http.get<AdminWallet>(url, { headers })),
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  deleteWallet(id: string): Observable<any> {
    this.loadingSubject.next(true);
    const url = `${this.apiUrl}/${id}`;
    return from(this.getHeadersIfNeeded(url)).pipe(
      switchMap((headers) => this.http.delete<any>(url, { headers })),
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }
}
